import { Pipe, PipeTransform } from '@angular/core';

const BINARY_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const;

/**
 * Renders a byte count as a human-readable size using binary prefixes (1024-based) but the
 * conventional symbols users expect ("KB" / "MB" rather than "KiB" / "MiB"). Used in the
 * attachment chip + preview-dialog footer where the original bytes come from the
 * `CommentAttachment.sizeBytes` wire field (a stringified Long).
 *
 * The pipe is intentionally permissive about input: it accepts `number`, numeric `string`, or
 * `null`/`undefined`. Invalid / negative inputs resolve to an empty string so callers can drop
 * the size silently in degraded states (e.g. a deleted attachment row).
 */
@Pipe({ name: 'tfFormatBytes', standalone: true, pure: true })
export class FormatBytesPipe implements PipeTransform {
  transform(value: number | string | null | undefined, fractionDigits = 1): string {
    if (value === null || value === undefined) return '';
    const numeric = typeof value === 'string' ? Number(value) : value;
    if (!Number.isFinite(numeric) || numeric < 0) return '';
    if (numeric === 0) return '0 B';
    let size = numeric;
    let unit = 0;
    while (size >= 1024 && unit < BINARY_UNITS.length - 1) {
      size /= 1024;
      unit += 1;
    }
    // Bytes are always whole numbers; use no fraction for the unconverted bucket.
    const digits = unit === 0 ? 0 : fractionDigits;
    return `${size.toFixed(digits)} ${BINARY_UNITS[unit]}`;
  }
}
